export type CustomFetchOptions = RequestInit & {
  timeout?: number;
  keepAlive?: boolean;
};

export type CustomFetchConfig = {
  timeout?: number;
};

async function getClientAuthToken(): Promise<string | undefined> {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('user-session='))
    ?.split('=')[1];
  const userSession = cookie ? JSON.parse(decodeURIComponent(cookie)) : {};
  return userSession?.tokenFromResponse;
}

async function getAppFromHostname() {
  if (typeof window === 'undefined') {
    return;
  }

  const appFromCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('x-app'))
    ?.split('=')[1];
  if (appFromCookie) {
    return appFromCookie;
  }

  return window.location.hostname.split('.')[0];
}

const customFetchClientSide = async <T>(
  url: string,
  options: CustomFetchOptions = {},
  { timeout = 5000 }: CustomFetchConfig = {},
): Promise<T> => {
  const tokenFromResponse = await getClientAuthToken();
  const app = await getAppFromHostname();

  const method = options.method || 'GET';
  const hasBody = method !== 'GET' && method !== 'HEAD';

  let finalUrl = url;
  let finalBody = options.body;

  const headers: HeadersInit = {
    ...(options.headers || {}),
    ...(tokenFromResponse
      ? { Authorization: `Token ${tokenFromResponse}` }
      : {}),
    ...(app ? { app: app } : {}),
  };

  if (hasBody) {
    // Для FormData добавляем app как поле
    if (finalBody instanceof FormData) {
      if (app) {
        finalBody.append('app', app);
        console.log('finalBody formdata append app', finalBody);
      }
    }
    // Для JSON body добавляем app в объект
    else if (finalBody) {
      try {
        const bodyData =
          typeof finalBody === 'string' ? JSON.parse(finalBody) : finalBody;
        console.log('json stringify', finalBody);
        finalBody = JSON.stringify({ ...bodyData, app });
      } catch {
        // Если не JSON, добавляем app в query параметры
        const urlObj = new URL(url, window.location.origin);
        if (app) {
          urlObj.searchParams.set('app', app);
        }
        finalUrl = urlObj.toString();
      }
    } else {
      console.log('else block', finalBody);
      // Если body пустой, создаём JSON с app
      finalBody = JSON.stringify({ app });
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }
  } else {
    // Для GET/HEAD добавляем app в query параметры
    const urlObj = new URL(url, window.location.origin);
    if (app) {
      urlObj.searchParams.set('app', app);
    }
    finalUrl = urlObj.toString();
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    body: finalBody,
    credentials: options.credentials,
    cache: options.cache,
  };

  if (!url) {
    throw new TypeError('URL must be a non-empty string');
  }
  const response = await fetch(finalUrl, fetchOptions);

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error('Response is not JSON');
  }

  return (await response.json()) as T;
};

export { customFetchClientSide };
